import type { ErrorEvent } from '@sentry/react';

import { commonTrackingEvents, tracking } from './src/tracking';

const MAX_MESSAGE_LENGTH = 200;

// `tracking.onError` reports shipper failures through `captureException`, which
// re-enters `beforeSend` → this hook → the same shipper. The flag breaks that
// cycle. Sentry drops an event whose `beforeSend` throws, so the try/catch keeps
// a tracking failure from ever losing the error report.
const guard = { linking: false };

// Sentry ↔ session replay links. Every recorder with a recording in progress
// (`tracking.getReplays()`: OpenPanel on every session, Mixpanel while on)
// contributes `<recorder>.session_id` (Sentry-searchable) and `<recorder>.replay`
// (the deep link, shown in Slack via the alert rule's tags) and gets a
// `sentryError` marker on its timeline so the replay scrubs to the report.
export const linkSentryEventToReplays = (event: ErrorEvent): ErrorEvent => {
  if (guard.linking) {
    return event;
  }
  guard.linking = true;
  try {
    const replays = tracking.getReplays();
    if (replays.length === 0 || !event.event_id) {
      return event;
    }
    event.tags = {
      ...event.tags,
      ...Object.fromEntries(
        replays.flatMap(({ shipper, sessionId, url }) => [
          [`${shipper}.session_id`, sessionId],
          ...(url ? [[`${shipper}.replay`, url]] : [])
        ])
      )
    };
    const exception = event.exception?.values?.[0];
    tracking.track(
      commonTrackingEvents.sentryError,
      {
        sentryEventId: event.event_id,
        level: event.level,
        errorType: exception?.type,
        errorMessage: exception?.value?.slice(0, MAX_MESSAGE_LENGTH)
      },
      { only: replays.map((replay) => replay.shipper) }
    );
  } catch {
    // Best effort: the error report must reach Sentry whatever analytics does.
  } finally {
    guard.linking = false;
  }
  return event;
};
